import React, { ReactNode, useMemo } from "react";
import { FiX } from "react-icons/fi";
import { t, Trans } from "@lingui/macro";
import { Link } from "react-router-dom";
import cx from "classnames";
import { HeaderLink } from "./HeaderLink";
import "./Header.css";
import ExternalLink from "components/ExternalLink/ExternalLink";
import { ProtocolDropdown } from "components/Header/ProtocolDropdown/ProtocolDropdown";
import { MoreDropdown } from "components/Header/MoreDropdown/MoreDropdown";
import logoImg from "img/logo_palmswap.svg";
import externalLink from "img/ic_new_link_16.svg";
import copy from "img/ic_copy_16.svg";

import useMedia from "react-use/lib/useMedia";
import { useIsDevelopment } from "lib/useIsDevelopment";
import Button from "components/Button/Button";
import { helperToast } from "lib/helperToast";
import { identicon } from "minidenticons";
import { useWeb3React } from "@web3-react/core";
import { getAccountUrl, useENS } from "lib/legacy";
import { useChainId } from "lib/chains";
import { useCopyToClipboard } from "react-use";

type Props = {
  small?: boolean;
  clickCloseIcon?: () => void;
  redirectPopupTimestamp: number;
  showRedirectModal: (to: string) => void;
  isDrawer?: boolean;
  connectWallet?: () => void;
  active?: boolean;
  disconnectAccountAndCloseSettings?: () => void;
};

export function AppHeaderLinks({
  small,
  clickCloseIcon,
  redirectPopupTimestamp,
  showRedirectModal,
  isDrawer,
  connectWallet,
  active,
  disconnectAccountAndCloseSettings,
}: Props) {
  const isDevelopment = useIsDevelopment();

  const showNavLastMinus4 = useMedia("(min-width: 1100px)");
  const showNavLastMinus3 = useMedia("(min-width: 1200px)");
  const showNavLastMinus2 = useMedia("(min-width: 1300px)");
  const showNavLastMinus1 = useMedia("(min-width: 1300px)");

  const [, copyToClipboard] = useCopyToClipboard();

  const IdenticonImg = ({ username, saturation, lightness, ...props }) => {
    const svgURI = useMemo(
      () => "data:image/svg+xml;utf8," + encodeURIComponent(identicon(username, saturation, lightness)),
      [username, saturation, lightness]
    );
    return <img src={svgURI} alt={username} {...props} />;
  };

  // Two largest media queries should be same for better UX
  const showNavItem = React.useMemo(() => {
    return [showNavLastMinus4, showNavLastMinus3, showNavLastMinus2, showNavLastMinus1];
  }, [showNavLastMinus4, showNavLastMinus3, showNavLastMinus2, showNavLastMinus1]);

  const dontShowAnyLink = React.useMemo(() => {
    return showNavItem.some((el) => !el);
  }, [showNavItem]);

  const { account } = useWeb3React();
  const { ensName } = useENS(account);
  const { rawChainId } = useChainId();

  const accountUrl = getAccountUrl(rawChainId, account);

  const showNavItemAt = (index: number) => showNavItem.slice(index)[0];

  type LocalHeaderLinkProps = {
    className?: string;
    children: ReactNode;
    exact?: boolean;
    to: string;
    custom?: boolean;
    onClick?: () => void;
  };

  const LocalHeaderLink = React.useCallback(
    ({ to, exact, custom, className, children, ...restProps }: LocalHeaderLinkProps) => {
      return (
        <HeaderLink
          {...restProps}
          to={to}
          className={cx([
            className,
            {
              "header-link": !custom,
              "header-link--header": !isDrawer && !custom,
              "header-link--drawer": isDrawer && !custom,
            },
          ])}
          redirectPopupTimestamp={redirectPopupTimestamp}
          showRedirectModal={showRedirectModal}
        >
          {children}
        </HeaderLink>
      );
    },
    [isDrawer, redirectPopupTimestamp, showRedirectModal]
  );

  // type LocalExternalLinkProps = {
  //   className?: string;
  //   children: ReactNode;
  //   href: string;
  //   custom?: boolean;
  //   onClick?: () => void;
  // };

  // const LocalExternalLink = React.useCallback(
  //   ({ href, custom, className, children, ...restProps }: LocalExternalLinkProps) => {
  //     return (
  //       <ExternalLink
  //         {...restProps}
  //         href={href}
  //         className={cx([
  //           className,
  //           {
  //             "header-link": !custom,
  //             "header-link--header": !isDrawer && !custom,
  //             "header-link--drawer": isDrawer && !custom,
  //           },
  //         ])}
  //         custom={custom}
  //       >
  //         {children}
  //       </ExternalLink>
  //     );
  //   },
  //   [isDrawer]
  // );

  return (
    <div className="App-header-links">
      {small && (
        <div className="App-header-links-header">
          <Link className="App-header-link-main" to="/">
            <img src={logoImg} alt="Palmswap Logo" />

            <div>Palmswap</div>
          </Link>
          <div
            className="App-header-menu-icon-block mobile-cross-menu"
            onClick={() => clickCloseIcon && clickCloseIcon()}
          >
            <FiX className="App-header-menu-icon" />
          </div>
        </div>
      )}

      {!isDrawer && <ProtocolDropdown className="App-header-links-protocol-dropdown" />}

      <LocalHeaderLink to="/event">
        <Trans>Event</Trans>
      </LocalHeaderLink>

      <LocalHeaderLink
        to="/portfolio"
        className={cx({
          "opacity-20 pointer-events-none": !isDevelopment && !isDrawer,
          "opacity-50 pointer-events-none": !isDevelopment && isDrawer,
        })}
      >
        <Trans>Portfolio</Trans>
      </LocalHeaderLink>

      <LocalHeaderLink
        to="/trade"
        className={cx({
          "opacity-20 pointer-events-none": !isDevelopment && !isDrawer,
          "opacity-50 pointer-events-none": !isDevelopment && isDrawer,
        })}
      >
        <Trans>Trade</Trans>
      </LocalHeaderLink>

      {/* <LocalHeaderLink
        to="/dashboard"
        className={cx({
            "opacity-20 pointer-events-none": !isDevelopment && !isDrawer,
            'opacity-50 pointer-events-none': !isDevelopment && isDrawer,
          })}
      >
        <Trans>Dashboard</Trans>
      </LocalHeaderLink> */}

      <LocalHeaderLink
        to="/earn"
        className={cx({
          "opacity-20 pointer-events-none": !isDevelopment && !isDrawer,
          "opacity-50 pointer-events-none": !isDevelopment && isDrawer,
        })}
      >
        <Trans>Earn</Trans>
      </LocalHeaderLink>

      {(isDrawer || showNavItemAt(-2)) && (
        <LocalHeaderLink
          to="/buy"
          className={cx({
            "opacity-20 pointer-events-none": !isDevelopment && !isDrawer,
            "opacity-50 pointer-events-none": !isDevelopment && isDrawer,
          })}
        >
          <Trans>Buy</Trans>
        </LocalHeaderLink>
      )}

      {/* {(isDrawer || showNavItemAt(-3)) && (
        <LocalExternalLink
          href="https://app.palmswap.org/launchpad"
          className={cx({
            "opacity-20 pointer-events-none": !isDevelopment && !isDrawer,
            "opacity-50 pointer-events-none": !isDevelopment && isDrawer,
          })}
        >
          <Trans>Launchpad</Trans>
        </LocalExternalLink>
      )} */}

      {(isDrawer || showNavItemAt(-1)) && (
        <LocalHeaderLink to="/referral">
          <Trans>Referral</Trans>
        </LocalHeaderLink>
      )}

      {/* {(isDrawer || showNavItemAt(-1)) && (
        <LocalExternalLink
          href="https://app.palmswap.org/claim"
          className={cx({
            "opacity-20 pointer-events-none": !isDevelopment && !isDrawer,
            "opacity-50 pointer-events-none": !isDevelopment && isDrawer,
          })}
        >
          <Trans>Claim</Trans>
        </LocalExternalLink>
      )} */}

      {isDrawer && (
        <div className="button-container">
          {active ? (
            <>
              <div className="address-section">
                <p className="wallet-provider-wrapper">
                  <Trans>Account </Trans>
                  <span className="wallet-provider">
                    <Trans>(Metamask)</Trans>
                  </span>
                </p>
              </div>

              <div className="address-section">
                <div className="user-avatar Identicon-wrapper">
                  {active && (
                    <IdenticonImg
                      username={account}
                      saturation="80"
                      lightness="50"
                      width="40"
                      height="40"
                      className="Identicon"
                    />
                  )}
                </div>

                <div className="user-address-wrapper">
                  <span className="user-address strong">{ensName || account}</span>
                </div>
              </div>

              <div className="address-bottom  gap-5">
                <div className="">
                  <div className="address-bottom-item">
                    <ExternalLink href={accountUrl} className="address-link">
                      <p className="address-link transition">View in Explorer</p>
                    </ExternalLink>

                    <div className="address-icons-link ">
                      <img src={externalLink} alt="Open address in explorer" />
                    </div>
                  </div>
                </div>

                <div
                  className="address-bottom-item"
                  onClick={() => {
                    copyToClipboard(account as any);
                    helperToast.success(t`Address copied to your clipboard`);
                  }}
                >
                  <p>
                    <Trans>Copy Address</Trans>
                  </p>

                  <div className="address-icons-copy">
                    <img src={copy} alt="Copy user address" />
                  </div>
                </div>
              </div>
              <Button
                variant="secondary"
                size="lg"
                className="w-full "
                disabled={false}
                type="submit"
                onClick={disconnectAccountAndCloseSettings}
              >
                Disconnect
              </Button>
            </>
          ) : (
            <Button
              variant="primary-action"
              size="lg"
              className="w-full"
              disabled={false}
              type="submit"
              onClick={connectWallet}
            >
              Connect Wallet
            </Button>
          )}
        </div>
      )}
      {!isDrawer && dontShowAnyLink && (
        <MoreDropdown>
          {!showNavItemAt(-2) && (
            <LocalHeaderLink to="/buy" className={cx({ "opacity-50 pointer-events-none": !isDevelopment })} custom>
              <Trans>Buy</Trans>
            </LocalHeaderLink>
          )}

          {/* {!showNavItemAt(-3) && (
            <LocalExternalLink
              href="https://app.palmswap.org/launchpad"
              className={cx({ "opacity-50 pointer-events-none": !isDevelopment })}
              custom
            >
              <Trans>Launchpad</Trans>
            </LocalExternalLink>
          )} */}

          {!showNavItemAt(-1) && (
            <LocalHeaderLink to="/referral" custom>
              <Trans>Referral</Trans>
            </LocalHeaderLink>
          )}

          {/* {!showNavItemAt(-1) && (
            <LocalExternalLink
              href="https://app.palmswap.org/claim"
              className={cx({ "opacity-50 pointer-events-none": !isDevelopment })}
              custom
            >
              <Trans>Claim</Trans>
            </LocalExternalLink>
          )} */}
        </MoreDropdown>
      )}
    </div>
  );
}
